import { axiosInstance } from "@/config/axios-wrapper";
import { V2Response } from "@/types/api";
import { Meeting } from "@/types/meeting";
import { JobseekerResume } from "@/types/resume";
import {
  Certificate,
  Jobseeker,
  OnboardingStats,
  type CreateCertificatePayload,
  type UpdateUserPayload,
} from "@/types/user";

export const getUserNotifications = async (
  data: { userId: string },
  options: { cursor?: string },
) => {
  const response = await axiosInstance.get<V2Response<any>>(
    `${`/users/${data.userId}`}/notifications${options.cursor ? `?cursor=${options.cursor}` : ""}`,
  );

  return response.data.result;
};

export const loginUserWithEmailAndPassword = async (data: { email: string; password: string }) => {
  const response = await axiosInstance.post<V2Response<any>>("/users/login", data);
  return response.data.result;
};

export const getUser = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}`);
  return response.data.result;
};

export const deleteUser = async (userId: string, data: { reason: string }) => {
  const response = await axiosInstance.post<V2Response<any>>(`/users/${userId}/delete`, data);
  return response.data.result;
};

export const getJobseeker = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<Jobseeker>>(`/users/${userId}`);
  return response.data.result;
};

export const getEmployer = async (userId: string) => {
  const response = await axiosInstance.get<
    V2Response<{
      id: string;
      full_name: string;
      email: string;
      type: string;
      photo: string;
      job_title: string;
      address?: {
        line1: string;
        line2: string;
        country: string;
        state: string;
        city: string;
        zip_code: string;
      };
      organization?: {
        id: string;
        name: string;
        tax_id: string;
        logo: string;
      };
      organization_owner?: boolean;
      permissions: string[];
      password_changed: boolean;
    }>
  >(`/users/${userId}`);
  return response.data.result;
};

export const uploadUserVideo = async (userId: string, formData) => {
  const response = await axiosInstance.post<V2Response<any>>(`/users/${userId}/video`, formData);
  return response.data.result;
};

export const deleteUserVideo = async (userId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(`/users/${userId}/video`);
  return response.data.result;
};

export const uploadUserResume = async (userId: string, formData: FormData) => {
  const response = await axiosInstance.post<V2Response<any>>(`/users/${userId}/resume`, formData);
  return response.data.result;
};

export const deleteUserResume = async (userId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(`/users/${userId}/resume`);
  return response.data.result;
};

export const updateUser = async (userId: string, data: UpdateUserPayload) => {
  const response = await axiosInstance.put<V2Response<any>>(`/users/${userId}`, data);
  return response.data.result;
};

export const updateUser2 = async ({ id, data }: { id: string; data: UpdateUserPayload }) => {
  const response = await axiosInstance.put<V2Response<any>>(`/users/${id}`, data);
  return response.data.result;
};

export const uploadUserPhoto = async (userId: string, formData) => {
  const response = await axiosInstance.post<
    V2Response<{ photo: string; photo_thumbnail_url: string }>
  >(`/users/${userId}/photo`, formData);
  return response.data.result;
};

export const getOnboardingStats = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<OnboardingStats>>(
    `/users/${userId}/onboarding`,
  );
  return response.data.result;
};

export const sendChangeEmailOtp = async (userId: string, data: { email: string }) => {
  const response = await axiosInstance.post<V2Response<{ token: string }>>(
    `/users/${userId}/email/send-otp`,
    data,
  );
  return response.data.result;
};

export const verifyChangeEmailOtp = async (
  userId: string,
  data: { token: string; otp: string },
) => {
  const response = await axiosInstance.post<V2Response<any>>(
    `/users/${userId}/email/verify-otp`,
    data,
  );
  return response.data.result;
};

export const changeUserPassword = async (
  userId: string,
  data: { old_password?: string; new_password: string },
) => {
  const payload: { old_password?: string; new_password: string } = {
    new_password: data.new_password,
  };
  if (data.old_password) {
    payload.old_password = data.old_password;
  }
  const response = await axiosInstance.put<V2Response<any>>(`/users/${userId}/password`, payload);
  return response.data.result;
};

export const getUserVisitCount = async (id: string) => {
  const response = await axiosInstance.get<V2Response<{ visit_count: number }>>(
    `/users/${id}/visit-count`,
  );
  return response.data.result;
};

export const getUserMeetings = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<Meeting[]>>(`/users/${userId}/meetings`);
  return { meetings: response.data.result };
};

export const updateUserReferrals = async ({ source }: { source: string }) => {
  const response = await axiosInstance.post<V2Response<any>>("/user-refferals", { source });
  return response.data.result;
};

export const getUserParsedResumeWithAI = async (data: FormData) => {
  const response = await axiosInstance.post<V2Response<JobseekerResume>>(`/ai/parse-resume`, data);
  return response.data.result;
};

/**
 * Quickstart: generate an "About me" blurb (and optionally normalized
 * certificates) from raw question answers. Backend is expected to compose a
 * short profile summary via OpenAI and return any structured fields that
 * should be applied to the jobseeker profile.
 */
export type GenerateAboutMePayload = {
  job_title?: string;
  experience_years?: number;
  location?: string;
  skills?: string[];
  certifications?: string[];
};

export type GenerateAboutMeResult = {
  about_me?: string;
  certificates?: Array<{ name: string; issuer_name?: string }>;
};

export const generateAboutMeFromQuestions = async (data: GenerateAboutMePayload) => {
  const response = await axiosInstance.post<V2Response<GenerateAboutMeResult>>(
    `/ai/generate-about-me`,
    data,
  );
  return response.data.result;
};

export const updateUserOnboardingPopups = async (userId: string, resume: boolean) => {
  const response = await axiosInstance.patch<V2Response<any>>(`/users/${userId}/popups`, {
    popups: { resume },
  });
  return response.data.result;
};

export const searchUserByEmail = async (email: string) => {
  const response = await axiosInstance.get<V2Response<any>>(
    `/users/search-by-email?email=${email}`,
  );
  return response.data.result;
};

// Get list of certificates
export const getCertificates = async () => {
  const response = await axiosInstance.get<V2Response<any>>(`/other/certificates`);
  return response.data.result;
};

// Parse certificate using AI
export const parseCertificate = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosInstance.post<V2Response<any>>("/ai/parse-certificate", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data.result;
};

// Create new certificate
export const createCertificate = async (userId: string, data: CreateCertificatePayload) => {
  const response = await axiosInstance.post<V2Response<any>>(`/users/${userId}/certificate`, data);
  return response.data.result;
};

// Get user certificates that are already uploaded
export const getUserCertificates = async (userId: string) => {
  const response = await axiosInstance.get<V2Response<any>>(`/users/${userId}/certificates`);
  return response.data.result;
};

// Update user certificates via PUT /v2/users/:userId with certificates in body
export const updateUserCertificates = async (
  userId: string,
  certificates: Partial<Certificate>[],
) => {
  const response = await axiosInstance.put<V2Response<any>>(`/users/${userId}`, {
    certificates,
  });
  return response.data.result;
};

// Delete specific certificate
export const deleteCertificate = async (userId: string, certificateId: string) => {
  const response = await axiosInstance.delete<V2Response<any>>(
    `/users/${userId}/certificate/${certificateId}`,
  );
  return response.data.result;
};

// Register FCM token
export const registerFcmToken = async (userId: string, token: string) => {
  const response = await axiosInstance.post<V2Response<any>>(`/users/${userId}/fcm-token`, {
    token,
  });
  return response.data.result;
};
